import { PAGE_API_QUERY_KEY_HEAD } from '@Core/clients/pageApiQueryKeys';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import { withNotificationRecipient } from '@Pages/pageRuntime/sdk/notificationRecipient';
import type {
  ApiError,
  SdkSendResolvedNotificationHandle,
  SdkSendResolvedNotificationInput,
  SdkSendNotificationResponse,
  SdkUseSendEmailOptions
} from '@Pages/pageRuntime/sdk/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { NOTIFICATION_HISTORY_QUERY_KEY } from './useNotificationHistory';

export const SEND_RESOLVED_NOTIFICATION_URL = '/api/email-messages/send-resolved-notification';

/**
 * Preview returns `sms_body` for an SMS-enabled template, and published pages spread that
 * draft into an email send that omits `delivery_channel`. The API prohibits `sms_body` on an
 * email send, so drop it here. An explicit `delivery_channel` stays unchanged for validation.
 */
function withoutDefaultSmsBody(input: SdkSendResolvedNotificationInput) {
  if (input.delivery_channel !== undefined) {
    return input;
  }
  const { sms_body: _smsBody, ...email } = input;

  return email;
}

export function useSendResolvedNotification(
  options?: SdkUseSendEmailOptions
): SdkSendResolvedNotificationHandle {
  const queryClient = useQueryClient();
  const mutation = useMutation<
    SdkSendNotificationResponse,
    ApiError,
    SdkSendResolvedNotificationInput
  >({
    mutationFn: (input) =>
      axios
        .post<SdkSendNotificationResponse>(
          SEND_RESOLVED_NOTIFICATION_URL,
          withoutDefaultSmsBody(withNotificationRecipient(input))
        )
        .then(
          (response) => response.data,
          (error: unknown) => {
            throw toApiError(error);
          }
        ),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['email-messages', 'search'] });
      void queryClient.invalidateQueries({
        queryKey: [PAGE_API_QUERY_KEY_HEAD, NOTIFICATION_HISTORY_QUERY_KEY]
      });

      options?.onSuccess?.(response);
    },
    onError: (error) => options?.onError?.(error)
  });

  return {
    send: (input) =>
      mutation.mutateAsync(input).then(
        (response) => response ?? null,
        () => null
      ),
    data: mutation.data,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: () => {
      mutation.reset();
    }
  };
}
